"use client";

import { useEffect, useRef } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";

interface SelectSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Changes to this value mean the option list was replaced (for example after a
   * server-side search resolved). Radix moves focus onto an item when the
   * content's children change, so we restore focus to the search box.
   */
  refocusKey?: unknown;
}

/**
 * Search box rendered inside a Radix <SelectContent>.
 *
 * Radix Select is a listbox, not a combobox: it claims keyboard events that
 * bubble out of its content and runs typeahead on them, which moves DOM focus
 * onto the first matching option. That is why typing here used to jump out of
 * the field as soon as a letter matched an item. Stopping propagation for every
 * key except the ones the dropdown legitimately owns (Escape to close, Tab to
 * leave) keeps the caret in the input.
 *
 * The pointer handlers serve the same purpose for mouse interaction - without
 * them Radix treats a click on the input as a click on the list.
 */
export function SelectSearchInput({
  value,
  onChange,
  placeholder = "Search...",
  disabled = false,
  refocusKey,
}: SelectSearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (disabled) {
      return;
    }
    if (document.activeElement !== inputRef.current) {
      inputRef.current?.focus();
    }
  }, [refocusKey, disabled]);

  const keepKeyboardEventsInsideInput = (
    event: React.KeyboardEvent<HTMLElement>,
  ) => {
    if (event.key !== "Escape" && event.key !== "Tab") {
      event.stopPropagation();
    }
  };

  return (
    <div
      className="sticky top-0 z-10 bg-popover border-b px-1 py-2"
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={keepKeyboardEventsInsideInput}
    >
      <div className="relative">
        <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="pl-8 h-9"
          disabled={disabled}
          autoFocus
          onKeyDown={keepKeyboardEventsInsideInput}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
}
